import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { useCalendarPreview, useCalendarProfiles, useUpdateCalendar } from "./api";
import type { CalendarEntry, CalendarProfile } from "../../types/directory";
import { Authority } from "../../auth/authorities";
import { useAuth } from "../../auth/useAuth";
import { useNotify } from "../../app/useNotify";
import { errorMessageFor } from "../../api/client";
import { formatDateTimeWithZone } from "../../lib/format";
import { usePageTitle } from "../../app/usePageTitle";

export function CalendarPage() {
  usePageTitle("Calendar approvals");
  const { can } = useAuth();
  const notify = useNotify();
  const profiles = useCalendarProfiles();
  const update = useUpdateCalendar();
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [removing, setRemoving] = useState<CalendarEntry | null>(null);
  const preview = useCalendarPreview(previewId);

  async function setApproved(calendar: CalendarEntry, approved: boolean) {
    try {
      await update.mutateAsync({ calendarId: calendar.id, patch: { approved } });
      notify.success(approved ? "Calendar approved" : "Calendar unapproved");
    } catch (error) {
      notify.error(errorMessageFor(error) ?? "Could not update the calendar.");
    }
  }

  function group(title: string, rows: CalendarProfile[], approved: boolean) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
          {title} ({rows.reduce((count, row) => count + row.calendars.length, 0)})
        </Typography>
        <Paper>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Profile</TableCell>
                  <TableCell>Organisation type</TableCell>
                  <TableCell>Calendar</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography sx={{ py: 3, textAlign: "center" }} color="text.secondary">
                        {approved ? "No approved calendars yet." : "Nothing waiting for approval."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {rows.flatMap((profile) =>
                  profile.calendars.map((calendar, index) => (
                    <TableRow key={calendar.id} hover>
                      <TableCell>{index === 0 ? profile.profile : ""}</TableCell>
                      <TableCell>{index === 0 ? profile.org_type : ""}</TableCell>
                      <TableCell>
                        <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
                          {calendar.name}
                          {!calendar.sync_enabled && (
                            <Chip size="small" label="Sync paused" color="warning" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" sx={{ justifyContent: "flex-end", gap: 1 }}>
                          <Button size="small" onClick={() => setPreviewId(calendar.id)}>
                            Preview
                          </Button>
                          {can(Authority.ApproveCalendar) && (
                            <Button
                              size="small"
                              variant={approved ? "outlined" : "contained"}
                              onClick={() => setApproved(calendar, !approved)}
                              disabled={update.isPending}
                            >
                              {approved ? "Unapprove" : "Approve"}
                            </Button>
                          )}
                          {can(Authority.ApproveCalendar) && calendar.sync_enabled && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => setRemoving(calendar)}
                            >
                              Stop sync
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )),
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  }

  if (profiles.isLoading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {group("Awaiting approval", profiles.data?.unapproved ?? [], false)}
      <Divider sx={{ mb: 3 }} />
      {group("Approved", profiles.data?.approved ?? [], true)}

      <Dialog open={previewId !== null} onClose={() => setPreviewId(null)} fullWidth maxWidth="sm">
        <DialogTitle>{preview.data?.calendar?.name ?? "Calendar"} preview</DialogTitle>
        <DialogContent>
          {preview.isLoading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (preview.data?.events ?? []).length === 0 ? (
            <Typography color="text.secondary">No events found in the last 7 days</Typography>
          ) : (
            (preview.data?.events ?? []).map((event, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  {event.summary || "Untitled Event"}
                </Typography>
                <Typography variant="body2">
                  {formatDateTimeWithZone(event.start?.dateTime ?? event.start?.date)} —{" "}
                  {formatDateTimeWithZone(event.end?.dateTime ?? event.end?.date)}
                </Typography>
                {event.description && <Typography variant="body2">{event.description}</Typography>}
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewId(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        title={`Pause syncing "${removing?.name}"?`}
        message="It stays listed, but no new events are pulled in until you resume it."
        confirmLabel="Pause sync"
        destructive
        busy={update.isPending}
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (!removing) return;
          try {
            await update.mutateAsync({
              calendarId: removing.id,
              patch: { sync_enabled: false },
            });
            notify.success("Sync paused");
          } catch (error) {
            notify.error(errorMessageFor(error) ?? "Could not stop the sync.");
          } finally {
            setRemoving(null);
          }
        }}
      />
    </Box>
  );
}
